import { Badge, Box, Chip } from '@mui/material';
import {
  JSXElementConstructor,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useMemo
} from 'react';
import CloseIcon from '@mui/icons-material/Close';
import { CSSProperties } from '@mui/styles';
import { styleMappings } from '../../Styles/AgTypography';
import { AgColor } from '../../Styles/AgColor';
import { useIntl } from 'react-intl';
import HomeIcon from '@mui/icons-material/Home';

import { useAppSelector } from '../../../redux/hooks';
import { selectThemeColors } from '../../../redux/slices/global';

const AgChip = ({
  label,
  onClick,
  onDelete,
  xIcon,
  icon,
  type = 'default',
  subType,
  badgeContent,
  transparent,
  style,
  exampleIcon,
  icon_badge_side,
  badgeBGColor,
  badgeOn,
  customBadgeContent
}: {
  label: string | ReactNode;
  onClick?: MouseEventHandler<HTMLDivElement>;
  onDelete?: (() => void) | undefined;
  xIcon?: boolean;
  icon?: ReactElement<any, string | JSXElementConstructor<any>> | undefined;
    type?: 'members' | 'staff' | 'organizations' | 'certifications' | 'generic' | 'fields' | 'default' | 'male' | 'female' | 'gender_unknown' | 'non_binary';
  subType?: 'not_captured' | 'invalid' | 'default';
  badgeContent?: number;
  transparent?: boolean;
  exampleIcon?: boolean;
  style?: CSSProperties;
  icon_badge_side?: 'left' | 'right';
  badgeBGColor?: string;
  badgeOn?: boolean;
  customBadgeContent?: ReactNode | string | number;
}) => {
  const intl = useIntl();
  useAppSelector(selectThemeColors);

  const chipColors: { [key: string]: string } = useMemo(
    () => ({
      members: AgColor.turquoiseLight,
      staff: AgColor.purpleLight,
      organizations: AgColor.blueLight,
      certifications: AgColor.grey7,
      generic: AgColor.grey2,
      fields_not_captured: AgColor.grey2,
      fields_invalid: AgColor.errorLight,
      fields_default: AgColor.pinkLight,
      default: AgColor.secondaryLight,
      male: AgColor.turquoiseLight,
      female: AgColor.purpleLight,
      gender_unknown: AgColor.blueLight,
      non_binary: AgColor.grey3,
    }),
    []
  );

  const badgeColors: { [key: string]: string } = useMemo(
    () => ({
      members: AgColor.turquoiseMain,
      staff: AgColor.purpleMain,
      organizations: AgColor.blueMain,
      certifications: AgColor.grey7,
      generic: AgColor.grey7,
      fields_not_captured: AgColor.grey7,
      fields_invalid: AgColor.errorMain,
      fields_default: AgColor.pinkMain,
      default: AgColor.secondaryDark,
      male: AgColor.turquoiseMain,
      female: AgColor.purpleMain,
      gender_unknown: AgColor.blueMain,
      non_binary: AgColor.grey7,
    }),
    []
  );

  const color = useMemo(
    () => ({
      chip: chipColors[
        !!type && /^(fields)$/gi.test(type)
          ? `${type}_${subType || 'default'}`
          : type || 'default'
      ],
      badge:
        badgeColors[
          !!type && /^(fields)$/gi.test(type)
            ? `${type}_${subType || 'default'}`
            : type || 'default'
        ],
    }),
    [type, subType, chipColors, badgeColors]
  );

  const myBadge = (
    <Badge
      max={1000000000}
      badgeContent={customBadgeContent ?? intl.formatNumber(badgeContent || 0)}
      sx={{
        '& .MuiBadge-badge': {
          ...styleMappings['AP.H5.UPPERCASE.Medium'],
          borderRadius: '5px',
          bgcolor: badgeBGColor || color.badge,
          position: 'relative',
          top: '10px',
          right: '50%'
        }
      }}
      style={{
        color: badgeBGColor ? color.badge : AgColor.grey0
      }}
    />
  );

  const myCloseIcon = (
    <CloseIcon
      style={{ color: AgColor.grey0 }}
      sx={{
        bgcolor: color.badge,
        borderRadius: '5px',
        width: '16px',
        height: '16px'
      }}
    />
  );

  const myExampleImg = <HomeIcon style={{ color: color.badge }}
    sx={{
      // bgcolor: color.badge,
      borderRadius: '5px',
      width: '16px',
      height: '16px'
    }}/>;

  return (
    <Box onClick={onClick} sx={{ padding: '8px 0px', width: 'fit-content' }}>
      <Chip
        onMouseDown={(event) => {
          event.stopPropagation();
        }}
        style={style}
        label={label}
        onDelete={() => {
          if ((!badgeContent || badgeContent <= 0 || !badgeOn) && onDelete) onDelete();
        }}
        deleteIcon={
          badgeContent !== undefined && badgeContent >= 0 && badgeOn && icon_badge_side !== 'left' ? (
            myBadge
          ) : xIcon && icon_badge_side !== 'left' ? (
            myCloseIcon
          ) : icon_badge_side === 'right' && icon ? (
            icon
          ) : icon_badge_side === 'right' && exampleIcon ? (
            myExampleImg
          ) : (
            <></>
          )
        }
        icon={
          icon && icon_badge_side !== 'right' ? (
            icon
          ) : exampleIcon && icon_badge_side !== 'right' ? (
            myExampleImg
          ) : icon_badge_side === 'left' && badgeContent !== undefined && badgeOn? (
            myBadge
          ) : icon_badge_side === 'left' && xIcon ? (
            myCloseIcon
          ) : (
            <></>
          )
        }
        variant='filled'
        sx={{
          bgcolor: transparent ? 'transparent' : color.chip,
          display: 'flex',
          justifyContent: 'center',
          borderRadius: '5px',
          minWidth: '79',
          minHeight: '32px',
          '& .MuiChip-label': {
            ...styleMappings['AP.H5.Medium'],
            color: (type === 'default' && !transparent) ? AgColor.grey0 : color.badge,
            padding: '0px',
            display: 'block',
            whiteSpace: 'normal'
          },
          '&.MuiChip-root': {
            height: 'fit-content',
            padding: '0px',
            columnGap: '6px',
            paddingRight:
              icon_badge_side === 'right' || myCloseIcon || badgeContent || badgeOn
                ? '8px'
                : '0px',
            paddingLeft:
              icon_badge_side === 'left' || myExampleImg ? '8px' : '0px',
            justifyContent: 'space-between'
          },
          '.MuiChip-deleteIcon': {
            margin: '0px'
          },
          '.MuiChip-icon': {
            margin: '0px'
          }
        }}
      />
    </Box>
  );
};

export default AgChip;
